import { Controller } from "@hotwired/stimulus"

/**
 * Expand/collapse to-do row + keep mobile/desktop checkbox mirrors in sync.
 * Optional completeUrl is unused when blank; listen for `todo-item:toggled`
 * / `todo-item:completed` for product wiring later.
 */
export default class extends Controller {
  static targets = ["toggle", "panel", "chevron", "checkbox", "checkboxMirror", "mobileFooterDivider"]
  static values = {
    expanded: { type: Boolean, default: false },
    title: { type: String, default: "" },
    completeUrl: { type: String, default: "" },
    submitMethod: { type: String, default: "POST" }
  }

  connect() {
    this.sync()
    this.syncCheckboxFromPrimary()
  }

  toggle(event) {
    event?.preventDefault()
    this.expandedValue = !this.expandedValue
    this.dispatch("toggled", { detail: { expanded: this.expandedValue } })
  }

  expandedValueChanged() {
    this.sync()
  }

  syncCheckbox(event) {
    const source = event?.currentTarget
    if (!source) return

    const checked = !!source.checked
    this.checkboxTargets.forEach((el) => {
      if (el !== source) el.checked = checked
    })
    this.checkboxMirrorTargets.forEach((el) => {
      if (el !== source) el.checked = checked
    })

    this.dispatch("completed", { detail: { checked } })
    this.persistComplete(checked)
  }

  syncCheckboxFromPrimary() {
    if (!this.hasCheckboxTarget || !this.hasCheckboxMirrorTarget) return

    const checked = !!this.checkboxTarget.checked
    this.checkboxMirrorTargets.forEach((el) => {
      el.checked = checked
    })
  }

  sync() {
    const expanded = this.expandedValue

    this.element.classList.toggle("todo-item--expanded", expanded)

    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute("aria-expanded", String(expanded))
      this.toggleTarget.setAttribute("aria-label", this.expandToggleLabel(expanded))
    }

    if (this.hasPanelTarget) {
      this.panelTarget.classList.toggle("hidden", !expanded)
      this.panelTarget.classList.toggle("flex", expanded)
      this.panelTarget.setAttribute("aria-hidden", String(!expanded))
      if (expanded) {
        this.panelTarget.removeAttribute("inert")
      } else {
        this.panelTarget.setAttribute("inert", "")
      }
    }

    if (this.hasMobileFooterDividerTarget) {
      this.mobileFooterDividerTarget.classList.toggle("hidden", !expanded)
    }

    if (this.hasChevronTarget) {
      this.chevronTarget.classList.toggle("rotate-180", expanded)
    }
  }

  persistComplete(checked) {
    if (!this.completeUrlValue) return

    window.fetch(this.completeUrlValue, {
      method: (this.submitMethodValue || "POST").toUpperCase(),
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      },
      body: JSON.stringify({ complete: checked })
    }).catch(() => {})
  }

  expandToggleLabel(expanded) {
    const action = expanded ? "Collapse" : "Expand"
    const title = this.titleValue?.trim()
    return title ? `${action} details for ${title}` : `${action} to-do details`
  }
}
